import React, { useState, useContext,useEffect } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import {
  Input,
  FloatingInput,
  InlineRadioToggle,
  Upload,
  DropDownSelect,
  PhoneInputField
} from '../FormikInputs'
import { FileUploadComponent } from '../../pages/Home/WBPSteps/step1'
import { checkOverseasUser, getDocObj, getTokenAndUid, handlePageLoadEventLog, saveSession } from '../../lib/utils'
import { post } from '../../lib/api'
import Loader from '../Loader'
import PencilIcon from '../Images/PencilIcon'
import GenericError from '../GenericError'
import { AccountContext } from '../../providers/accountProvider'
import { Box } from '../../designSystem'
import SubHeader from '../WithSubHeaderBG'
import AlertSnackBar from '../AlertSnackBar'
import { allLanguages } from '../../pages/Home/constants'

const BasicDiv = styled.div`
  padding: 24px;
  margin-bottom: 8px;
  background-color: var(--Dove);
  @media(max-width:600px){
    width: 100vw;
  }
`
const InvalidDetailsDiv = styled.div`
  color: red;
`;
const BasicDetailsDiv = styled(BasicDiv)`
  position: relative;
`
const ProfileImageDiv = styled.div`
  position: absolute;
  right: 16px;
  top: 7px;
  height: 112px;
  width: 112px;
  border-radius: 50%;
  box-shadow: inset 0 0 4px 0 rgba(0, 0, 0, 0.16);
  background-color: var(--LD5);
  
  img {
    height: 112px;
    width: 112px;
    border-radius: 50%;
  }
  .Upload input {
    display: none;
  }

  @media(max-width: 600px){
    margin-right: 30%;
  }
`
const EditIconDiv = styled.div`
  position: absolute;
  top: -75px;
  right: 35px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    fill: var(--Dove);
  }
`
const SectionHeading = styled.div`
  font-size: 20px;
  line-height: 1.2;
  text-align: left;
  color: var(--Text700);
  margin-bottom: 16px;
`
const BankDetailsDiv = styled(BasicDiv)``
const DocumentsDiv = styled(BasicDiv)``
const AddressDiv = styled(BasicDiv)`
  margin-bottom: 24px;
`

const ActionButtonBox = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 48px;
  @media(max-width: 600px){
    margin-right: 4px;
  }
`
const SaveButton = styled.button`
  cursor:${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  border: none;
  outline: none;
  background: none;
  border-radius: 4px;
  background-color: var(--Azure500);
  font-size: 14px;
  font-weight: bold;
  line-height: 1.43;
  color: var(--Dove);
  padding: 16px;
  width: 200px;
  opacity: ${({ disabled }) => disabled ? 0.3 : 1};
`
const LabelDiv = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: var(--Text700);
  margin-bottom: 8px;
  margin-top: 8px;
`

const ImgUploadCTA = ({ handleReUpload }) => (
  <EditIconDiv onClick={handleReUpload}>
    <PencilIcon />
  </EditIconDiv>
)

const ProfileUI = ({ reset, userData, isOverseasUser, ...props }) => {
  const [apiError, toggleApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaved,setIsSaved] = useState(false)
  const [phoneAlert, showPhoneAlert] = useState(false)
  const {user,onUpdateUser, isUgcUser} = useContext(AccountContext)
  const [authorLanguages,setAuthorLanguages] = useState(allLanguages)
  const isMobile = window.screen.width < 600
  useEffect(() =>{
    handlePageLoadEventLog('profile')
    if(user?.author_visibility === 'row'){
      setAuthorLanguages(allLanguages.filter(lang => lang.name === 'english'))
    }else if(user?.author_visibility === 'india' && isOverseasUser){
      setAuthorLanguages([
        {id: 1, name: 'hindi' },
        {id: 2, name: 'english'},
        {id: 3, name: 'tamil'},
        {id: 4, name: 'bengali'}])
    }
  },[])
  const removeEmptyKey=(object)=>{
    for(const [key] of Object.entries(object)){
      if(object[key]===""){
          delete object[key];
      }
    }
    return object
  }

  return (
  <SubHeader title="Edit Profile">
    <Formik
      initialValues={{
        fullname: userData.fullname || '',
        phone_number: userData.phone_number || '',
        gender: userData.gender || '',
        image_url: userData.image_url,
        email: userData.email || '',
        bio: userData.bio || '',
        languages: userData.languages || '',
        account_type: userData.account_type || '',
        account_name: userData?.account_name || '',
        bank_name: userData?.bank_name || '',
        account_no: userData?.account_no || '',
        ifsc_code: userData?.ifsc_code || '',
        pan_card_no: userData?.pan_card_no || '',
        cancelled_cheque_url: userData?.cancelled_cheque_url ||  '',
        pan_card_url: userData?.pan_card_url || '',
        address_1: userData.address_1 || '',
        address_2: userData.address_2 || '',
        pin_code: userData.pin_code || '',
        city: userData.city || '',
        state: userData.state || '',
        swift_code : userData?.swift_code || '',
        gov_id_no : userData?.gov_id_no || '',
        govt_id_url : userData?.govt_id_url ||  '',
        f10: userData.f10 || '',
        npec: userData.npec || '',
        w9: userData.w9 || '',
        tin: userData.tin || '',
        trc: userData.trc || '',
        paypal_id : userData.paypal_id || '',
        aadhaar_card_no: userData.aadhaar_card_no || '',
        aadhaar_card_url: userData.aadhaar_card_url || '',
        guardian_phone_number: userData.guardian_phone_number || '',
        guardian_email: userData.guardian_email || ''
      }}
      validationSchema={Yup.object({
        account_name : userData.is_guardian_account ? Yup.string().required('Required').matches(/^[\D]*$/, 'Only Alphabets are  allowed') :  Yup.string().when(['cancelled_cheque_url','bank_name','account_no','swift_code','gov_id_no','govt_id_url','pan_card_url','tin','trc','f10','npec','w9'],
          (cancelled_cheque_url,pan_card_url,govt_id_url,account_no,bank_name,swift_code,gov_id_no,tin,trc,npec,f10,w9,schema) => (
              !isOverseasUser ? (cancelled_cheque_url || pan_card_url) : 
                isUgcUser ||(cancelled_cheque_url || govt_id_url || bank_name || account_no || swift_code || gov_id_no || tin || trc || npec || f10 || w9 )) ? 
                  schema.required(['Required']) :  schema),
        account_no : userData.is_guardian_account ? Yup.string().required('Required').matches(/^\d{9,18}$/, 'Please Enter Valid Account Number') :  Yup.string().when(['cancelled_cheque_url','bank_name','swift_code','gov_id_no','govt_id_url','pan_card_url','tin','trc','f10','npec','w9'],
          (cancelled_cheque_url,pan_card_url,govt_id_url,bank_name,swift_code,gov_id_no,tin,trc,npec,f10,w9,schema) => (
            !isOverseasUser ? (cancelled_cheque_url || pan_card_url) : 
            isUgcUser || (cancelled_cheque_url || govt_id_url || bank_name  || swift_code || gov_id_no || tin || trc || npec || f10 || w9 )) ? 
                  schema.required(['Required']) :  schema),
        bank_name : Yup.string().when(['cancelled_cheque_url','swift_code','gov_id_no','govt_id_url','pan_card_url','tin','trc','f10','npec', 'w9'],
          (cancelled_cheque_url,pan_card_url,govt_id_url,swift_code,gov_id_no,tin,trc,npec,f10,w9,schema) => (
          !isOverseasUser ? (cancelled_cheque_url || pan_card_url) : 
              isUgcUser || (cancelled_cheque_url || govt_id_url  || swift_code || gov_id_no || tin || trc || npec || f10 || w9 )) ? 
                schema.required(['Required']) :  schema),
        swift_code: isUgcUser && Yup.string().required('Required'),
        gov_id_no: isUgcUser && Yup.string().required('Required'),
        pan_card_no : Yup.string().matches(
          /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
          "Invalid Pan Card Number"
        ),
        phone_number : Yup.string().required('Required'),
        email: Yup.string().required('Required').email('Enter valid Email ID'),
        guardian_email : userData.is_guardian_account && Yup.string().required('Required').email('Enter valid Email ID').notOneOf([Yup.ref('email')],"can't be the same as the author’s email id"),
        ifsc_code: userData.is_guardian_account && Yup.string().required('Required').matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Enter Valid IFSC code'),
        aadhaar_card_no: userData.is_guardian_account && Yup.string().required('Required').matches(/^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/,"Invalid Aadhaar Number"),
        tin: isOverseasUser && !isUgcUser && Yup.string().required('Required'),
        f10: isOverseasUser && !isUgcUser && Yup.string().required('Required'),
        trc: isOverseasUser && !isUgcUser && Yup.string().required('Required'),
        npec: isOverseasUser && !isUgcUser && Yup.string().required('Required'),
        paypal_id: isUgcUser && Yup.string().required('Required')
      })}
      onSubmit={(values, { setSubmitting }) => {
        const { uid } = getTokenAndUid()
        const { account_id, address_id } = userData
        const {
          fullname,
          gender,
          email,
          phone_number,
          bio,
          image_url,
          languages,
          address_1,
          address_2,
          pin_code,
          city,
          state,
          account_name,
          account_type,
          bank_name,
          account_no,
          ifsc_code,
          pan_card_no,
          cancelled_cheque_url,
          pan_card_url,
          swift_code,
          gov_id_no,
          paypal_id,
          govt_id_url,
          tin,
          w9,
          f10,
          npec,
          trc,
          aadhaar_card_no,
          aadhaar_card_url
        } = values

        const userPostData = removeEmptyKey({
          fullname,
          email,
          phone_number,
          bio,
          gender,
          image_url,
        })
        const creatorPostData = {
          languages: languages || userData.languages || '',
          account_type: account_type || userData.account_type,
          user_id: uid,
          device_id: localStorage.getItem('uuid')
        }
        const addressPostData = {
          address_1,
          address_2,
          pin_code,
          city,
          state,
          address_id,
          uid,
        }
        
        let bankPostData = {
          account_name,
          bank_name,
          account_no,
        }
        let documents = []
        if(!isOverseasUser){
          bankPostData = {...bankPostData,account_id,ifsc_code,pan_card_no,pan_card_url,cancelled_cheque_url,uid}
        }else{
          if(cancelled_cheque_url){
            documents = [...documents, {
              'cancelled_cheque_url' : getDocObj('cancelled_cheque_url',cancelled_cheque_url,userData.bankDetailDocuments)
            }]
          }
          if(govt_id_url){
            documents = [...documents, {
              'govt_id_url' : getDocObj('govt_id_url',govt_id_url,userData.bankDetailDocuments)
            }]
          }
          if(f10){
            documents = [...documents, {
              'f10' : getDocObj('f10',f10,userData.bankDetailDocuments)
            }]
          }
          if(npec){
            documents = [...documents, {
              'npec' : getDocObj('npec',npec,userData.bankDetailDocuments)
            }]
          }
          if(tin){
            documents = [...documents, {
              'tin' : getDocObj('tin',tin,userData.bankDetailDocuments)
            }]
          }
          if(w9){
            documents = [...documents, {
              'w9' : getDocObj('w9',w9,userData.bankDetailDocuments)
            }]
          }
          if(trc){
            documents = [...documents, {
              'trc' : getDocObj('trc',trc,userData.bankDetailDocuments)
            }]
          }
          if(documents.length > 0){
            bankPostData.documents = documents
          }
          bankPostData.swift_code = swift_code
          bankPostData.paypal_id = paypal_id
          bankPostData.gov_id_no = gov_id_no
        }
        for (const variable in bankPostData) {
          if(!bankPostData[variable] && variable !== 'documents') delete bankPostData[variable]
        }
        if(Object.values(bankPostData).length > 0){
          if(userData?.id){
            bankPostData.id = userData?.id;
          }
          if(isOverseasUser) {
            bankPostData.creator_id = uid
          }
        }
        
        if(isOverseasUser){
          if (!cancelled_cheque_url) {
            delete bankPostData.cancelled_cheque_url
          }
  
          if (!pan_card_url) {
            delete bankPostData.pan_card_url
          }
        }
        setErrorMessage("");
        if(userData?.is_guardian_account){
          bankPostData.guardian_email = values?.guardian_email
          bankPostData.guardian_phone_number = values?.guardian_phone_number
        }
        const aadharData = {
          aadhaar_card_no, 
          aadhaar_card_url, 
          account_id : userData?.authorBankDetails?.account_id,
          uid,
          ifsc_code : userData?.authorBankDetails?.ifsc_code || '',
          bank_name : userData?.authorBankDetails?.bank_name || '',
          account_name : userData?.authorBankDetails?.account_name || '',
          account_no : userData?.authorBankDetails?.account_no || ''
        }
        const allPromises = [
          post('/user_api/user.update?is_novel=1', { data: {...userPostData } }),
          post('/user_api/user.create_creator_account?is_novel=1', {
            data: {...creatorPostData },
          }),
          post('/user_api/user_addresses/?is_novel=1', { data: {...addressPostData } }),
          isOverseasUser ?  Object.values(bankPostData).length > 0 && post(`/user_api/row_creator/?is_novel=1`, { data: {...bankPostData } }) : post(`/user_api/user_bank_accounts/?is_novel=1`, { data: {...bankPostData } }),
          userData?.is_guardian_account && post(`/user_api/user_bank_accounts/?is_novel=1`, { data: {...aadharData } })
        ]
        Promise.all(allPromises)
        .then(() => {
            saveSession({name:fullname, image_url})
            setIsSaved(true)
            setSubmitting(false)
            onUpdateUser({...user,account_type, languages})
          })
          .catch((error) => {
            setSubmitting(false)
            throw error
          })
      }}
    >
      {({ values = {}, isSubmitting, touched, submitCount, errors, }) => (
        <Form>
          <GenericError
            show={apiError}
            onClose={() => {
              toggleApiError(false)
            }}
          />
          <BasicDetailsDiv>
            <Box mb={[0,80]} variant="flex" flexDirection={["column","row"]}>
              <ProfileImageDiv>
                <img src={values.image_url} alt={values.image_url} />
                <Upload
                  name="image_url"
                  // renderComp={ImageUploadComponent}
                  renderComp={ImgUploadCTA}
                  imageValidator={(width, height) => width === height}
                  allowImageCrop
                  aspectRatio={1/1}
                  dimensionErrorMsg="The image does not follow the dimensions of the thumbnail. Please try with a different image."
                  fileTypeTags="image"
                  extraParams={{ title: 'thumbnail' }}
                />
                {/* <EditIconDiv><PencilIcon /></EditIconDiv> */}
              </ProfileImageDiv>
              <Box variant="flex" mt={[104,0]} >
                <SectionHeading>Basic Details</SectionHeading>
              </Box>
            </Box>
            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="fullname"
                  label="Your Name"
                  size="lg"
                />
              </Box>
              <Box width={["100%","49%"]} mb={16}>
                {/* <FloatingInput
                  type="text"
                  name="phone_number"
                  label="Mobile Number"
                  size="lg"
                  onClick={() => user?.login_type!=="email_login" && showPhoneAlert(true)}
                  readOnly = {user?.login_type!=="email_login"}
                  touched={touched?.phone_number}
                  hasError={submitCount > 0 && errors?.phone_number}
                /> */}
                <PhoneInputField
                  name="phone_number"
                  type="text"
                  onClick={() => user?.login_type!=="email_login" && showPhoneAlert(true)}
                  readOnly = {user?.login_type!=="email_login"}
                  touched={touched?.phone_number}
                  hasError={submitCount > 0 && errors?.phone_number}
                  country={user?.location?.toLowerCase() || 'in'}
                  showValidation
                />
              </Box>
            </Box>
            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <InlineRadioToggle
                  name="gender"
                  label="Gender"
                  options={[
                    {
                      label: 'Male',
                      name: 'male',
                    },
                    {
                      label: 'Female',
                      name: 'female',
                    },
                  ]}
                />
              </Box>
              <Box width={["100%","49%"]} mt={[16,0]} mb={16}>
                <FloatingInput
                  type="text"
                  name="email"
                  label="Email ID"
                  size="lg"
                  hasError={submitCount > 0 && errors.email}
                  touched={touched.email}
                  disabled={user?.login_type==="email_login"}
                />
              </Box>
            </Box>

            {!! (!checkOverseasUser(user)  && userData.is_guardian_account) && <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="aadhaar_card_no"
                  label="Aadhar No"
                  size="lg"
                  disabled={user?.login_type==="email_login"}
                />
              </Box>
              <Box width={["100%","49%"]} mt={[16,0]} mb={16}>
                <Upload
                  // label="Aadhar Document"
                  name="aadhaar_card_url"
                  privateDownload
                  hasError={submitCount > 0 && errors.aadhaar_doc}
                  hideInputOnValue
                  showProgress
                  saveWithoutCDN={1}
                  cloud_dir_file={getTokenAndUid().uid}
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: "Bank-Document"
                  }}
                />
              </Box>
            </Box>}
            <Box variant="flex" flexDirection={['column','row']}>
              <Input type="text" asTextArea name="bio" placeholder="About" />
            </Box>

            {!isUgcUser && !isMobile && <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]}>
                <LabelDiv>Language Proficiency</LabelDiv>
              </Box>
              {/* <Box width={["100%","49%"]}>
                <LabelDiv>Account Type</LabelDiv>
              </Box> */}
            </Box>}
            {!isUgcUser && <Box variant="flex" flexDirection={['column','row']}>
              {isMobile && <LabelDiv>Language Proficiency</LabelDiv>}
              <Box width={["100%","49%"]}>
                <DropDownSelect
                  name="languages"
                  selectByName
                  placeholder="Select a Language"
                  selectOptions={authorLanguages}
                  idKey="id"
                  nameKey="name"
                />
              </Box>
              {/* {isMobile && <LabelDiv>Account Type</LabelDiv>}
              <Box width={["100%","49%"]}>
                <RadioSelect
                  name="account_type"
                  options={[
                    {
                      name: "Author",
                      label: "Author"
                    },
                    {
                      name: "VO Artist",
                      label: "VO Artist"
                    },
                    {
                      name: "Publisher",
                      label: "Publisher"
                    }
                  ]}
                />
              </Box> */}
            </Box>}
          </BasicDetailsDiv>

          {!!(!checkOverseasUser(user) && userData?.is_guardian_account) && 
              <BasicDetailsDiv>
                <SectionHeading>Guardian's Detail</SectionHeading>
                <Box variant="flex" flexDirection={['column','row']}>
                  <Box width={["100%","49%"]}>
                    <FloatingInput
                      type="text"
                      name="guardian_email"
                      label="Guardian Email ID"
                      size="lg"
                      touched={touched.guardian_email}
                      hasError={submitCount > 0 && errors.guardian_email}
                    />
                  </Box>
                  <Box width={["100%","49%"]} mb={16}>
                    <FloatingInput
                      type="text"
                      name="guardian_phone_number"
                      label="Mobile Number"
                      size="lg"
                      disabled
                    />
                  </Box>
                </Box>
              </BasicDetailsDiv>
          }
          <BankDetailsDiv>
            <SectionHeading>{userData?.is_guardian_account ? "Guardian's Bank Details" : "Bank Details"}</SectionHeading>

            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="account_name"
                  label="Full Name ( Account Owner )"
                  size="lg"
                  touched={touched.account_name}
                  hasError={submitCount > 0 && errors.account_name}
                  showValidation
                />
              </Box>

              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="bank_name"
                  label="Bank Name"
                  size="lg"
                  hasError={submitCount > 0 && errors.bank_name}
                  touched={touched.bank_name}
                  showValidation
                />
              </Box>
            </Box>

            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="account_no"
                  label="Account Number"
                  size="lg"
                  hasError={submitCount > 0 && errors.account_no}
                  touched={touched.account_no}
                  showValidation
                />
              </Box>

              <Box width={["100%","49%"]} mb={16}>
                {!isOverseasUser ? <FloatingInput
                  type="text"
                  name="ifsc_code"
                  label="IFSC Code"
                  size="lg"
                  hasError={submitCount > 0 && errors.ifsc_code}
                /> :  
                <FloatingInput
                  type="text"
                  name="swift_code"
                  label="SWIFT Code"
                  size="lg"
                  touched={touched.swift_code}
                  hasError={submitCount > 0 && errors?.swift_code}
                  showValidation
                />}
              </Box>
            </Box>
            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%", "49%"]} mb={16}>
              {!isOverseasUser ?<FloatingInput
                  type="text"
                  name="pan_card_no"
                  label="Pan Card Number"
                  size="lg"
                  hasError={submitCount > 0 && errors.pan_card_no}
                /> : 
                  <FloatingInput
                    type="text"
                    name="gov_id_no"
                    label="Govt Id number"
                    size="lg"
                    touched={touched.gov_id_no}
                    hasError={submitCount > 0 && errors.gov_id_no}
                    showValidation
                  />
                }
              </Box>
              {isOverseasUser && <Box width={["100%", "49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="paypal_id"
                  label="Paypal ID"
                  size="lg"
                  touched={touched.paypal_id}
                  hasError={submitCount > 0 && errors.paypal_id}
                  showValidation
                />
              </Box>}
            </Box>
          </BankDetailsDiv>
          <DocumentsDiv>
            <SectionHeading>{userData?.is_guardian_account ? " Guardian’s Bank Proof Document"  : "Documents"}</SectionHeading>
            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]}>
                <Upload 
                  label="Passbook Copy or Cancelled Cheque"
                  name="cancelled_cheque_url"
                  privateDownload
                  hideInputOnValue
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  showProgress
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'Bank-Document',
                  }}
                />
              </Box>
              {!isOverseasUser ? <Box width={["100%","49%"]}>
                <Upload
                  label="Pan Card"
                  name="pan_card_url"
                  privateDownload
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  hideInputOnValue
                  showProgress
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'Pan Card',
                  }}
                />
              </Box> : <Box width={["100%","49%"]}>
                <Upload
                  label="Govt issued Identification card"
                  name="govt_id_url"
                  privateDownload
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  hideInputOnValue
                  showProgress
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'Govt Id Proof',
                  }}
                />
              </Box>}
            </Box>
          </DocumentsDiv>
          {isOverseasUser && <DocumentsDiv>
            <SectionHeading>Self Declaration Documents</SectionHeading>
            <Box variant="flex" flexDirection={['column','row']}>
              {user?.location !== 'US' && <Box width={["100%","49%"]}>
                <Upload
                  label="F10"
                  name="f10"
                  privateDownload
                  hideInputOnValue
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  showProgress
                  sampleFileUrl="https://d31b0xt3oaqqjh.cloudfront.net/row-docs/470d2f844b2ed3c3b9f25a8890d188ec286e3584.pdf"
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'F10 Self Declaration',
                  }}
                  extraNote = 'Self Declare the document by downloading template'

                  hasError={errors.f10}
                />
              </Box>}
              {user?.location !== 'US' && <Box width={["100%","49%"]}>
                <Upload
                  label="NPEC"
                  name="npec"
                  privateDownload
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  hideInputOnValue
                  showProgress
                  sampleFileUrl="https://d31b0xt3oaqqjh.cloudfront.net/row-docs/ad0bb71d0bf1b2ff0ef2d1084650ce02347944ed.pdf"
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'NPEC Self Declaration',
                  }}
                  extraNote = 'Self Declare the document by downloading template'
                  hasError={errors.npec}
                />
              </Box>}
            </Box>
            {user?.location === 'US' && <Box variant="flex" flexDirection={['column','row']} mt={16}>
              <Box width={["100%","49%"]}>
                <Upload
                  label="W9"
                  name="w9"
                  privateDownload
                  hideInputOnValue
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  showProgress
                  sampleFileUrl="https://radiolycontent.s3.ap-southeast-1.amazonaws.com/cms/fw9.pdf"
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'W9 Self Declaration',
                  }}
                  hasError={errors.w9}
                  extraNote = 'Self Declare the document by downloading template'

                />
              </Box>
            </Box>}
            <Box variant="flex" flexDirection={['column','row']} mt={16}>
              <Box width={["100%","49%"]}>
                <Upload
                  label="TIN"
                  name="tin"
                  privateDownload
                  hideInputOnValue
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  showProgress
                  sampleFileUrl="https://d31b0xt3oaqqjh.cloudfront.net/row-docs/531582eada6b9413a108619739fcd29606688e08.pdf"
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'TIN Self Declaration',
                  }}
                  hasError={errors.tin}
                  extraNote = 'Self Declare the document by downloading template'

                />
              </Box>
            </Box>
          </DocumentsDiv>}
          {isOverseasUser && user.location !== 'US' && <DocumentsDiv>
            <SectionHeading>Government Issued Documents</SectionHeading>
            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]}>
                <Upload
                  label="TRC"
                  name="trc"
                  privateDownload
                  hideInputOnValue
                  cloud_dir_file={getTokenAndUid().uid}
                  saveWithoutCDN={1}
                  showProgress
                  placeholder="Drop your doc file here to upload or click to select"
                  renderComp={FileUploadComponent}
                  fileTypeTags="user_doc"
                  accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword, application/doc, application/ms-doc, image/jpeg, image/jpg, image/png, application/pdf"
                  extraParams={{
                    title: 'TRC',
                  }}
                  hasError={errors.trc}
                />
              </Box>
            </Box>
          </DocumentsDiv>}
          <AddressDiv>
            <SectionHeading>Mailing Address</SectionHeading>

            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="address_1"
                  label="House No, Apt No, Building No"
                  size="lg"
                />
              </Box>

              <Box width={["100%","49%"]} mb={16}>
                <FloatingInput
                  type="text"
                  name="address_2"
                  label="Address Line 2"
                  size="lg"
                />
              </Box>
            </Box>

            <Box variant="flex" flexDirection={['column','row']}>
              <Box width={["100%","32%"]} mb={16}>
                <FloatingInput
                  type="number"
                  name="pin_code"
                  label="Pincode"
                  size="lg"
                />
              </Box>

              <Box variant="flex" width={["100%","67%"]}>
                <Box width="49%" mb={16}>
                  <FloatingInput type="text" name="city" label="City" size="lg" />
                </Box>

                <Box width="49%" mb={16}>
                  <FloatingInput
                    type="text"
                    name="state"
                    label="State"
                    size="lg"
                  />
                </Box>
              </Box>
            </Box>
          </AddressDiv>
          <ActionButtonBox>
            {!isSubmitting && (
              <SaveButton type="submit" disabled={Object.keys(errors).length > 0}>Save Profile</SaveButton>
            )}
            {isSubmitting && (
              <SaveButton type="button">
                <Loader invertColor inline size="sm" />
              </SaveButton>
            )}
          </ActionButtonBox>
          {isSaved && <AlertSnackBar text="Saved Sucessfully" close={() => {
            setIsSaved(false)
            reset()
          }}/>}
          {phoneAlert && <AlertSnackBar text="Sorry, Phone number can't be changed" close={() => {
            showPhoneAlert(false)
          }}/>}
        </Form>
      )}
    </Formik>
  </SubHeader>
  )
}

export default ProfileUI
